"use client"

import * as React from "react"
import { Accordion as AccordionPrimitive } from "radix-ui"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/utilities/ui"
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react"

const accordionItemVariants = cva("", {
  variants: {
    variant: {
      default: "not-last:border-b",
      faq: "overflow-hidden rounded-xl border border-black/10 bg-white/70 scroll-mt-[calc(var(--mobile-header-bottom,5rem)+0.75rem)] lg:scroll-mt-0",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

const accordionTriggerVariants = cva(
  "group/accordion-trigger relative flex flex-1 items-start justify-between text-left outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "rounded-lg border border-transparent py-2.5 text-sm font-medium transition-all hover:underline focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:after:border-ring **:data-[slot=accordion-trigger-icon]:ml-auto **:data-[slot=accordion-trigger-icon]:size-4 **:data-[slot=accordion-trigger-icon]:text-muted-foreground",
        faq: "gap-6 px-6 py-5 font-outfit text-base font-semibold leading-5 text-dark-800/80 hover:no-underline focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 data-[state=open]:pt-6 data-[state=open]:pb-0 lg:px-10 lg:py-6 data-[state=open]:lg:pt-8",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

const accordionContentVariants = cva(
  "h-(--radix-accordion-content-height) [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4",
  {
    variants: {
      variant: {
        default: "pt-0 pb-2.5",
        faq: "px-6 pb-6 lg:px-10 lg:pb-8",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

type AccordionVariant = NonNullable<VariantProps<typeof accordionItemVariants>["variant"]>

const AccordionVariantContext = React.createContext<AccordionVariant>("default")

function Accordion({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root> & {
  variant?: AccordionVariant
}) {
  return (
    <AccordionVariantContext.Provider value={variant ?? "default"}>
      <AccordionPrimitive.Root
        data-slot="accordion"
        className={cn("flex w-full flex-col", className)}
        {...props}
      />
    </AccordionVariantContext.Provider>
  )
}

function AccordionItem({
  className,
  variant: variantProp,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> & {
  variant?: AccordionVariant
}) {
  const contextVariant = React.useContext(AccordionVariantContext)
  const variant = variantProp ?? contextVariant

  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(accordionItemVariants({ variant }), className)}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  variant: variantProp,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  variant?: AccordionVariant
}) {
  const contextVariant = React.useContext(AccordionVariantContext)
  const variant = variantProp ?? contextVariant

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(accordionTriggerVariants({ variant }), className)}
        {...props}
      >
        {children}
        {variant === "default" ? (
          <>
            <ChevronDownIcon data-slot="accordion-trigger-icon" className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden" />
            <ChevronUpIcon data-slot="accordion-trigger-icon" className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline" />
          </>
        ) : variant === "faq" ? (
          <ChevronDownIcon
            aria-hidden
            className="mt-0.5 size-5 shrink-0 text-dark-800/90 transition-transform duration-300 ease-in-out group-data-[state=open]/accordion-trigger:rotate-180"
          />
        ) : null}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  variant: variantProp,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & {
  variant?: AccordionVariant
}) {
  const contextVariant = React.useContext(AccordionVariantContext)
  const variant = variantProp ?? contextVariant

  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-open:animate-accordion-down data-closed:animate-accordion-up"
      {...props}
    >
      <div className={cn(accordionContentVariants({ variant }), className)}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
